import { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { api, track } from "../lib/api";
import { usePricing, productOf } from "../lib/catalog";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Checkbox } from "../components/ui/checkbox";
import { toast } from "sonner";
import { Check, Lock, Loader2, ArrowLeft, ShieldCheck, RefreshCw } from "lucide-react";
import ObjectionFAQ from "../components/ObjectionFAQ";

export default function Checkout() {
  const { planId } = useParams();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { data: pricing } = usePricing();
  const product = productOf(pricing, planId);
  const plan = product;
  const [docs, setDocs] = useState([]);
  const [accepted, setAccepted] = useState(false);
  const [email, setEmail] = useState("");
  const [company, setCompany] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/plans").then(({ data }) => setDocs(data.legal_documents)).catch(() => {});
    setEmail(localStorage.getItem("ar_lead_email") || "");
  }, [planId]);

  const opt = product?.options?.find((o) => o.id === params.get("billing")) || product?.options?.[0];
  useEffect(() => {
    if (!product) return;
    if (product.type === "custom_quote") navigate("/autonomous-commerce", { replace: true });
    else if (opt?.payment_link) window.location.href = opt.payment_link;
  }, [product, opt, navigate]);

  const startCheckout = async () => {
    if (!accepted) { toast.error("Please accept the agreements to continue"); return; }
    if (!email) { toast.error("Please enter your email"); return; }
    setLoading(true);
    try {
      const scanId = localStorage.getItem("ar_last_scan") || null;
      const { data } = await api.post("/payments/checkout", {
        plan_id: planId, billing: opt.id, origin_url: window.location.origin, scan_id: scanId,
        email, company, legal_accepted: true, accepted_documents: docs.map((d) => d.key),
      });
      track("checkout.started", { plan: planId });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start checkout");
      setLoading(false);
    }
  };

  if (!product || !opt) return <div className="min-h-screen flex items-center justify-center bg-slate-50"><Loader2 className="h-6 w-6 animate-spin text-indigo-600" /></div>;

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <button onClick={() => navigate(-1)} className="text-sm text-slate-500 hover:text-slate-800 flex items-center gap-1 mb-6"><ArrowLeft className="h-4 w-4" /> Back</button>
        <div className="grid lg:grid-cols-2 gap-8">
          <div className="rounded-2xl border border-slate-200 bg-white p-7">
            <p className="font-mono text-xs uppercase tracking-wide text-indigo-600">Order summary</p>
            <h1 className="font-heading text-2xl font-bold text-slate-900 mt-1">{plan.name}</h1>
            <p className="text-sm text-slate-500 mt-1">{plan.promise}</p>
            <p className="text-xs font-mono uppercase tracking-wide text-slate-400 mt-3" data-testid="checkout-option">{opt.label}</p>
            <div className="mt-4 space-y-2 border-t border-slate-100 pt-5">
              <div className="flex justify-between text-slate-700"><span>{opt.id === "plan" ? `Payment 1 of ${opt.installments} (due today)` : plan.type === "one_time" ? "One-time (due today)" : "Setup (one-time, due today)"}</span><span className="font-semibold">${Number(opt.due_today).toLocaleString()}</span></div>
              {opt.id === "plan" && <div className="flex justify-between text-slate-500 text-sm"><span>Then {opt.installments - 1} more monthly payment{opt.installments > 2 ? "s" : ""}</span><span>${Number(opt.installment_amount).toLocaleString()}/mo</span></div>}
              {opt.recurring && <div className="flex justify-between text-slate-500 text-sm"><span>Monitoring (begins after deployment)</span><span>${Number(opt.recurring.amount).toLocaleString()}/{opt.recurring.interval === "year" ? "yr" : "mo"}</span></div>}
              {plan.type === "one_time" && <p className="text-xs text-orange-600">{plan.credit_note}</p>}
            </div>
            <div className="mt-5 flex justify-between text-lg font-heading font-bold text-slate-900 border-t border-slate-100 pt-4">
              <span>Due today</span><span data-testid="checkout-total">${Number(opt.due_today).toLocaleString()}</span>
            </div>
            <ul className="mt-6 space-y-2">
              {plan.features.slice(0, 6).map((f, i) => <li key={i} className="flex items-start gap-2 text-sm text-slate-600"><Check className="h-4 w-4 text-emerald-500 mt-0.5 shrink-0" />{f}</li>)}
            </ul>
            <div className="mt-7 border-t border-slate-100 pt-6">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-4">What happens next</p>
              <ol className="space-y-4">
                {[
                  ["Instant confirmation", "You get a secure account and receipt the moment payment clears."],
                  ["We scan & implement", "Your deliverables are generated and applied — you approve before anything goes live."],
                  ["QA + before/after score", "An independent QA pass verifies the work, then monitoring begins."],
                ].map(([t, d], i) => (
                  <li key={t} className="flex gap-3" data-testid={`checkout-step-${i}`}>
                    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-indigo-600 text-white text-xs font-bold">{i + 1}</span>
                    <div><p className="text-sm font-semibold text-slate-900">{t}</p><p className="text-sm text-slate-500">{d}</p></div>
                  </li>
                ))}
              </ol>
            </div>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-7">
            <h2 className="font-heading text-lg font-bold text-slate-900">Complete your purchase</h2>
            <div className="mt-4 space-y-3">
              <Input data-testid="checkout-email" type="email" placeholder="Work email" value={email} onChange={(e) => setEmail(e.target.value)} className="h-12" />
              <Input data-testid="checkout-company" placeholder="Company name" value={company} onChange={(e) => setCompany(e.target.value)} className="h-12" />
            </div>
            <div className="mt-5 rounded-xl border border-slate-200 bg-slate-50 p-4 max-h-44 overflow-y-auto scrollbar-thin">
              <p className="text-xs font-semibold text-slate-700 mb-2">You are digitally accepting:</p>
              <ul className="space-y-1.5">
                {docs.map((d) => <li key={d.key} className="text-xs text-slate-500 flex items-center gap-2"><Check className="h-3 w-3 text-indigo-500" />{d.title}</li>)}
              </ul>
            </div>
            <label className="mt-4 flex items-start gap-3 cursor-pointer">
              <Checkbox checked={accepted} onCheckedChange={setAccepted} data-testid="checkout-accept" className="mt-0.5" />
              <span className="text-sm text-slate-600">I have read and agree to all agreements above, including recurring-payment terms and authorization to scan my website and make the specified changes.</span>
            </label>
            <Button onClick={startCheckout} disabled={loading} data-testid="checkout-pay-btn" className="w-full h-13 py-6 mt-5 rounded-full bg-indigo-600 hover:bg-indigo-700 font-semibold text-base">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Lock className="h-4 w-4 mr-2" /> Pay ${Number(opt.due_today).toLocaleString()} with Stripe</>}
            </Button>
            <div className="mt-5 grid grid-cols-3 gap-2 text-center" data-testid="checkout-trust-row">
              <div className="flex flex-col items-center gap-1"><Lock className="h-4 w-4 text-slate-400" /><span className="text-[11px] text-slate-500 leading-tight">Secure Stripe checkout</span></div>
              <div className="flex flex-col items-center gap-1"><RefreshCw className="h-4 w-4 text-slate-400" /><span className="text-[11px] text-slate-500 leading-tight">Cancel monitoring anytime</span></div>
              <div className="flex flex-col items-center gap-1"><ShieldCheck className="h-4 w-4 text-slate-400" /><span className="text-[11px] text-slate-500 leading-tight">Independent QA on every deliverable</span></div>
            </div>
            <p className="text-[11px] text-slate-400 mt-4 text-center">
              Secure payment via Stripe · Ajakaiye Innovation Holdings LLC ·{" "}
              <button type="button" onClick={() => navigate("/refund-policy")} className="underline hover:text-slate-600" data-testid="checkout-refund-link">Refund policy</button> ·{" "}
              <button type="button" onClick={() => navigate("/terms")} className="underline hover:text-slate-600" data-testid="checkout-terms-link">Terms</button>
            </p>
          </div>
        </div>
        <ObjectionFAQ className="mt-16" />
      </div>
    </div>
  );
}
